// Phase 20 — shared Timeline section for CaseView and CollectiveCaseView.
//
// Replaces the two near-duplicate inline Timeline blocks. Identical behavior
// in both views:
//   * Inputs: cs.openDate / pendingDate / closedDate / archivedDate, agenda
//     events filtered by parent id, time entries filtered by parent id,
//     cs.docs, cs.stages startDates, and per-case chat messages.
//   * Two kind-branches: chat-store key (cs.id for case vs "cc:"+cs.id for
//     collective) and the time-entry filter (e.caId for case vs e.ccId for
//     collective). The previous CollectiveCaseView timeline filtered by
//     e.caId on a collective case — that bug is fixed here.
//   * Month-label locale follows window.__lxLang (fr-BE / nl-BE / en-GB) —
//     CaseView's behavior; CollectiveCaseView previously hard-coded "en".
//   * Paginates 10 per page in both views (CaseView's behavior).
//   * Collapses via SectionHeader's left chevron, driven by secCol.timeline
//     (default collapsed in both views).
//
// Display-only — no save handlers, no entry points. canViewTimeline gate is
// the caller's responsibility; this component renders null if false.

import React, { useState } from "react";
import {
  FileText,
  Clock,
  Calendar,
  Archive,
  ChevronRight,
  MessageCircle,
  Send,
  Pencil,
} from "lucide-react";
import { useTranslation } from "../i18n";
import { fd, fmin, coIni } from "../utils";
import { ChatClock, chatRowMoment, newestFirst } from "../lib/chatTime"; // 207B §3 — the chat clock, a chat row's local day and moment
import { SectionHeader, InitialsBadge, ListNotLoaded } from "./SharedUI";
import { PAGE_LISTS } from "../lib/listLoad"; // 211 §10 — the chronicle and the mailings, when they could not load
import { useTaxonomy } from "../hooks/useTaxonomy";
import { getTaskStatusMeta, resolveTaxonomyLabel } from "../lib/taxonomyHelpers";
import type { TFn } from "../lib/taxonomyTypes"; // 184 A.4 — the `move` arm's `t`
import { pickEntryLabel } from "../utils/taxonomyMap";
import { eisenLabel } from "../EisenBadge";
import { RAD, FS } from "../lib/tokens";
// 186 B.7a [RULED 186 (10)] — the pager is a shared mechanism now: `PG = 10`,
// the `page` state, `tot` / `cur` / the slice and the `.csPgr` control moved
// to hooks/usePager.ts + components/Pager.tsx, and this section calls them.
import { usePager, pageOf } from "../hooks/usePager";
import Pager from "./Pager";
// 186 (d) [RULED 186 (16)] — the one search field the three sections share.
import SectionSearch from "./SectionSearch";
import { withCount } from "../lib/pluralVars"; // 209 §7 — a sentence's number, read once, as its placeholder and as `count`

/* 211 §10 — the page lists this section shows and counts (its host asks them, src/hooks/usePageList.ts):
   while one is not loaded, the words and « Réessayer » stand where the timeline, its count and its last row
   were — never a count short of the edits, never « No events recorded ». */
const TL_LISTS = [PAGE_LISTS.chronicle, PAGE_LISTS.mailings];

/* 186 (d) — the merged event's read-set, as `renderEvent` reads it (the `any`
   ratchet may never grow: the row renderer is typed on what it touches). */
interface TimelineEvent {
  date?: string | null;
  // 207B §3 — a chat row's moment: its clock after the date, its place within the day.
  ts?: number;
  type: string;
  icon?: React.ReactNode;
  text?: string;
  actorId?: number | null;
  actorName?: string | null;
}

const TL_COLORS = {
  case: "var(--ac)",
  status: "var(--am)",
  agenda: "var(--pp)",
  time: "var(--gn)",
  doc: "var(--ts)",
  stage: "var(--ac)",
  chat: "var(--tm)",
  mail: "var(--pp)",
  edit: "var(--am)",
};

// Phase 28 (item B) — short, human field labels for the chronology line. Keys
// match the LOG_FIELDS the server records; unmapped keys fall back to a
// camelCase→spaced humanisation.
const FIELD_LABELS = {
  /* 108 §8e — the English of the three status-companion date keys, like every
     row above: handed to t() as its fallback, never rendered directly. */
  pendingDate: "suspended since",
  closedDate: "closed on",
  archivedDate: "archived on",
  title: "title",
  status: "status",
  type: "type",
  priority: "priority",
  dueDate: "due date",
  date: "date",
  time: "time",
  notes: "notes",
  assigned: "assignee",
  delegatedAssigned: "delegated assignee",
  caseEisenhower: "Eisenhower",
  ref: "reference",
  openDate: "open date",
  introductionDate: "introduction date",
  jurisdictionId: "jurisdiction",
  claimAmount: "claim amount",
  description: "description",
  costsRatio: "costs ratio",
  referenceRate: "reference rate",
  referenceHourlyRate: "hourly rate",
  successFeeRate: "success fee",
};
// BRIEF 87 §1e — the per-collaborator billing ratio is logged as
// `caseCollaborator.<id>.ratio`, the id in the FIELD so old/new stay bare
// numbers a census can parse. Rendering it needs the id turned back into a
// person, which is why this takes the collaborator list: `humanizeField` alone
// would emit "case Collaborator.7.ratio" through the camelCase splitter.
const RATIO_FIELD_RX = /^caseCollaborator\.(\d+)\.ratio$/;
type CoRef = { id?: number | string | null; initials?: string; name?: string; lastName?: string };
const humanizeField = (f: any, collaborators?: CoRef[]) => {
  const m = RATIO_FIELD_RX.exec(String(f || ""));
  if (m) {
    const co = (collaborators || []).find((c: CoRef) => String(c.id) === m[1]);
    const who = co ? (co.initials || co.name || co.lastName || `#${m[1]}`) : `#${m[1]}`;
    return `billing ratio (${who})`;
  }
  return (
    (FIELD_LABELS as any)[f] ||
    String(f || "")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/^./, (c) => c.toLowerCase())
  );
};

// SC3-R5 — REUSE existing trilingual field-label keys (case-edit form / table
// headers) instead of duplicating; only caseEisenhower needs its own key.
const FIELD_KEY = {
  title: "field.title",
  status: "field.status",
  type: "field.type",
  priority: "field.priority",
  dueDate: "field.due_date",
  date: "field.date",
  time: "field.time",
  notes: "field.notes",
  assigned: "case.field.assigned",
  delegatedAssigned: "timeline.field.delegated",
  caseEisenhower: "timeline.field.eisenhower",
  ref: "case.field.ref",
  openDate: "field.open_date",
  /* 108 §8e — the status-companion dates joined CASE_LOG_FIELDS; their labels
     are the modal's own, already seeded en/fr/nl. */
  pendingDate: "field.pending_since",
  closedDate: "field.closed_on",
  archivedDate: "field.archived_on",
  introductionDate: "case.field.introductionDate",
  jurisdictionId: "field.jurisdiction",
  claimAmount: "modal.case.claim_amount",
  description: "common.description",
  costsRatio: "modal.case.costs_ratio_pct",
  referenceRate: "case.terms.reference_rate",
  referenceHourlyRate: "modal.case.reference_hourly_rate",
  successFeeRate: "case.claim.success_fee",
};
// Entity nouns are rendered mid-sentence, so they get dedicated sentence-form
// keys (the column-header keys are capitalised / article-less and read poorly
// inside a sentence).
const ENTITY_SLUG = {
  Task: "task",
  AgendaEvent: "event",
  Case: "case",
  CollectiveCase: "collectiveCase",
  TimeEntry: "timeEntry",
  Document: "document",
  // Round 13 (C73/C72) — subtask lifecycle + task documents are chronicled.
  Subtask: "subtask",
  TaskDoc: "taskDoc",
};
const ENTITY_FALLBACK = {
  Task: "the task",
  AgendaEvent: "the event",
  Case: "the case",
  CollectiveCase: "the collective case",
  TimeEntry: "the time entry",
  Document: "the document",
  Subtask: "the subtask",
  TaskDoc: "the task document",
};

// Phase 25 — ONE summary line per collective bulk mailing (NOT per-client).
// 207C §7 (extract, rule 3) — the mailing rows' builder, MOVED out of the
// component's body unchanged (it pushed onto `tlEvents`; it returns its rows,
// as `buildEditEvents` does) so a recording can run it; both are exported for
// that alone. `mailings` and `t` arrive as the component's own (`unknown` in,
// read as they were read).
export function buildMailEvents(mailings: readonly unknown[] | null | undefined, translate: unknown): TimelineEvent[] {
  const t = translate as TFn;
  const out: TimelineEvent[] = [];
  (mailings || []).forEach((mm: any) => {
    const when = mm.finishedAt || mm.startedAt || mm.createdAt;
    if (!when) return;
    const modeLabel = t("case.timeline.mail_mode." + (mm.mode || "individual"), {
      fallback:
        mm.mode === "draft" ? "draft" : mm.mode === "merge" ? "merge" : "individual",
    });
    const parts = [
      t("case.timeline.mail_recipients", withCount(mm.totalCount, "n", { fallback: mm.totalCount + " recipients",
      })),
      mm.mode === "draft"
        ? t("case.timeline.mail_drafted", {
            n: mm.sentCount,
            fallback: mm.sentCount + " drafted",
          })
        : t("case.timeline.mail_ok", {
            n: mm.sentCount,
            fallback: mm.sentCount + " ok",
          }),
      t("case.timeline.mail_failed", withCount(mm.failedCount, "n", { fallback: mm.failedCount + " failed",
      })),
    ];
    if (mm.skippedCount)
      parts.push(
        t("case.timeline.mail_skipped", withCount(mm.skippedCount, "n", { fallback: mm.skippedCount + " skipped",
        })),
      );
    const subjectPart = mm.subject ? " — " + mm.subject : "";
    out.push({
      /* 207C §7 — a mailing's LOCAL day and its moment, as a chat row's (src/lib/chatTime
         `chatRowMoment`, 207B §3): `toISOString()` filed a mailing sent at 00:30 in
         Brussels under the day before; the moment puts its clock after the date and
         orders the day's rows by time. */
      ...chatRowMoment(when),
      type: "mail",
      icon: <Send size={10} />,
      text: t("case.timeline.bulk_mail", {
        mode: modeLabel,
        subject: subjectPart,
        summary: parts.join(", "),
        fallback:
          "Bulk mailing (" + modeLabel + ") — " + parts.join(", ") + subjectPart,
      }),
    });
  });
  return out;
}

// 184 A.4 — the chronology row as the `move` arm reads it. The rest of
// `buildEditEvents` is untyped (its inputs are `any`); this names what the new
// arm touches, and the app's `t` is typed there by the IMPORTED `TFn` (183:
// a function type written here with named parameters is two `no-unused-vars`).
interface MoveRow { field?: string | null; oldValue?: string | null; newValue?: string | null }

// Group append-only chronology rows by groupId into ONE timeline event each
// (multi-field saves share a groupId). Returns {date,type:'edit',icon,text}.
// SC3-R5 — render each chronology entry through PARAMETERIZED PER-LANGUAGE
// templates (the sentence shape is language-specific so word order reads
// naturally), routing action verbs, field labels, entity nouns and enum
// old→new values through i18n via the `h` helpers built in the component.
export function buildEditEvents(chronicle: any, t: any, h: any) {
  const groups = new Map();
  (chronicle || []).forEach((r: any) => {
    const key = r.groupId || "g:" + r.id;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(r);
  });
  const out = [];
  for (const rows of groups.values()) {
    const head = rows[0];
    // The actor is rendered as an InitialsBadge in the row, so the text omits
    // the actor prefix; actorName is carried as a badge fallback.
    const label = head.entityLabel ? `“${head.entityLabel}”` : "";
    const entity = h.entityNoun(head.entity);
    const target = label || entity; // archive/restore/purge: prefer the label
    let text;
    if (head.action === "create") {
      text = t("timeline.create", { entity, label, fallback: `created ${entity} ${label}` });
    } else if (head.action === "archive") {
      text = t("timeline.archive", { target, fallback: `archived ${target}` });
    } else if (head.action === "unarchive") {
      text = t("timeline.unarchive", { target, fallback: `restored ${target}` });
    } else if (head.action === "done") {
      // Round 13 (C73) — subtask completion verbs (server writes
      // 'done'/'undone' on the done-flag flip instead of a boolean diff).
      text = t("timeline.done", { target, fallback: `completed ${target}` });
    } else if (head.action === "undone") {
      text = t("timeline.undone", { target, fallback: `reopened ${target}` });
    } else if (head.action === "purge") {
      text = t("timeline.purge", { target, fallback: `permanently deleted ${target}` });
    } else if (head.action === "prospect-convert") {
      // 140 §2 — the switch's own chronicle row. The ref old→new travels in
      // the row's changes[] and stays readable there; the sentence names the
      // event rather than falling through to a bare "changed …".
      text = t("timeline.prospect_convert", { target, fallback: `converted ${target} into a real case` });
    } else if (head.action === "prospect-convert-undo") {
      text = t("timeline.prospect_convert_undo", { target, fallback: `undid the conversion of ${target}` });
    } else if (head.action === "move") {
      // 184 A.4 — a task or event changed matter (lib/matterMove.js writes the
      // same row on BOTH matters' timelines, so each side reads one sentence).
      // The refs travel in the row's own changes[] (`matter`, old → new); a
      // matterless side is `—` as the server wrote it. Typed at the seam
      // (`MoveRow`, `TFn`) so the arm adds no `any` and the ratchets
      // (any-count, eslint) do not rise.
      const mv = (rows as MoveRow[]).find((r) => r.field === "matter") ?? (head as MoveRow);
      const oldRef = mv.oldValue ?? "—";
      const newRef = mv.newValue ?? "—";
      const tg = String(target);
      text = (t as TFn)("timeline.move", {
        target: tg,
        old: oldRef,
        new: newRef,
        fallback: `moved ${tg} from ${oldRef} to ${newRef}`,
      });
    } else {
      // 'edit' — one clause per changed field; enum old/new values resolved.
      const clauses = rows
        .filter((r: any) => r.field)
        .map((r: any) =>
          t("timeline.clause", {
            field: h.fieldLabel(r.field),
            old: h.resolveValue(head.entity, r.field, r.oldValue),
            new: h.resolveValue(head.entity, r.field, r.newValue),
            fallback: `${h.fieldLabel(r.field)} from ${r.oldValue ?? "∅"} to ${r.newValue ?? "∅"}`,
          }),
        );
      const joined = clauses.join("; ");
      if (clauses.length) {
        text = label
          ? t("timeline.changed", { label, clauses: joined, fallback: `changed ${label}: ${joined}` })
          : t("timeline.changed_nolabel", { clauses: joined, fallback: `made changes: ${joined}` });
      } else {
        text = label
          ? t("timeline.changed_only", { label, fallback: `changed ${label}` })
          : t("timeline.changed_only_nolabel", { fallback: "made changes" });
      }
    }
    out.push({
      /* 207C §7 — an edit's LOCAL day and its moment, as a chat row's (`chatRowMoment`):
         `createdAt.slice(0, 10)` is UTC's day, so an edit made just after midnight in
         Brussels sat under the day before, while its clock (207B's, from this same
         `ts`) read 00:05. The moment is a number now, as the day's order reads it. */
      ...(head.createdAt ? chatRowMoment(head.createdAt) : { date: "" }),
      type: "edit",
      icon: <Pencil size={10} />,
      text: String(text).replace(/\s{2,}/g, " ").trim(),
      actorId: head.actorId ?? null,
      actorName: head.actorName || null,
    });
  }
  return out;
}

const LOCALE_BY_LANG = { en: "en-GB", fr: "fr-BE", nl: "nl-BE" };
const activeLocale = () =>
  (LOCALE_BY_LANG as any)[(typeof window !== "undefined" && window.__lxLang) || "en"] ||
  "en-GB";

export default function CaseTimelineSection({
  kind, // 'case' | 'cc'
  cs, // case or collective-case entity
  ag = [],
  te = [],
  co = [],
  chatMsgs = {},
  mailings = [], // Phase 25 — collective bulk mailings (cc kind only)
  chronicle = [], // Phase 28 (item B) — append-only edit/create/archive log rows
  currentUser, // collaborator id of the viewer — for the actor badge's "isMe" ring
  canViewTimeline = true,
  sectionOpen, // boolean — driven by secCol.timeline (default collapsed)
  onSectionToggle,
}: any) {
  const { t } = useTranslation();
  const pager = usePager();
  // 186 (d) [RULED 186 (16)] — the search field's query: the events this
  // section holds, filtered in the client on the sentence and the actor.
  const [q, setQ] = useState("");
  // SC3-R5 — taxonomies for resolving enum old→new values to trilingual labels
  // (module-cached, so multiple reads are cheap). Hooks must precede the
  // canViewTimeline early-return below (rules of hooks).
  const { entries: taxTaskStatus } = useTaxonomy("task_status");
  const { entries: taxCaseStatus } = useTaxonomy("case_status");
  const { entries: taxTaskPriority } = useTaxonomy("item_level");
  const { entries: taxActivity } = useTaxonomy("activity_code");
  const { entries: taxEventType } = useTaxonomy("event_type");
  const { entries: taxCaseType } = useTaxonomy("case_type");
  const { entries: taxCaseStage } = useTaxonomy("case_stage");

  if (!canViewTimeline) return null;

  const isCC = kind === "cc";
  const parentField = isCC ? "ccId" : "caId";
  const chatKey = isCC ? "cc:" + cs.id : cs.id;

  // SC3-R6 — shared resolvers for the whole timeline (merged entries below +
  // the edit-log helpers). Date VALUES are formatted with the same Belgian
  // formatter (fd) the date column uses, so no raw ISO / no time component.
  const byCode = (entries: any, raw: any) => {
    if (raw == null || raw === "" || raw === "∅") return raw;
    const e = (entries || []).find((x: any) => x && String(x.code) === String(raw));
    return e ? pickEntryLabel(e) : raw;
  };
  const DATE_VALUE_FIELDS = new Set([
    "dueDate",
    "date",
    "openDate",
    "introductionDate",
  ]);

  // ── Event merge ────────────────────────────────────────────────────────────
  const tlEvents: any[] = [];

  if (cs.openDate)
    tlEvents.push({
      date: cs.openDate,
      type: "case",
      icon: <FileText size={10} />,
      text: t("case.timeline.case_opened", {
        ref: cs.ref,
        title: cs.title,
        fallback: "Case opened: " + cs.ref + " — " + cs.title,
      }),
    });
  if (cs.pendingDate)
    tlEvents.push({
      date: cs.pendingDate,
      type: "status",
      icon: <Clock size={10} />,
      text: t("case.timeline.status_pending", {
        fallback: "Status changed to Pending",
      }),
    });
  if (cs.closedDate)
    tlEvents.push({
      date: cs.closedDate,
      type: "status",
      icon: <Clock size={10} />,
      text: t("case.timeline.case_closed", { fallback: "Case closed" }),
    });
  if (cs.archivedDate)
    tlEvents.push({
      date: cs.archivedDate,
      type: "status",
      icon: <Archive size={10} />,
      text: t("case.timeline.case_archived", { fallback: "Case archived" }),
    });

  // Agenda events filtered by parent id.
  (ag || [])
    .filter((a: any) => a[parentField] === cs.id)
    .forEach((ev: any) => {
      tlEvents.push({
        date: ev.date,
        type: "agenda",
        icon: <Calendar size={10} />,
        text:
          (ev.type ? byCode(taxEventType, ev.type) + ": " : "") +
          ev.title +
          (ev.time
            ? t("case.timeline.at", {
                time: ev.time,
                fallback: " at " + ev.time,
              })
            : ""),
      });
    });

  // Time entries filtered by parent id. CollectiveCaseView previously read
  // e.caId on a collective case — that was the bug. The correct filter on a
  // collective case is e.ccId (matches ccMetrics' bookkeeping).
  (te || [])
    .filter((e: any) => e[parentField] === cs.id)
    .forEach((e: any) => {
      /* 158 §7 CORRECTED — date · collaborator · TYPE · description · time,
         which is what the table's header now reads too, so this row matches it
         by construction rather than by a reconciliation of its own.

         Two things it did not need: a new string, because the row ALREADY has
         a person slot between the icon and the text (the edit events'
         `actorId` → InitialsBadge below), and a new resolver, because
         `taxActivity` was already loaded at the top of this component and
         `byCode` already turns a code into its label — the pair had simply
         never been pointed at a time entry. `by X` therefore leaves the
         sentence and `e.coId` fills the slot; a coId `co` cannot resolve
         renders nothing, exactly as the old `c ? … : ""` did.

         Every segment is optional and omits IN PLACE: an entry with no type
         and no description is still "45m logged", never a leading dash. */
      const logged = t("case.timeline.time_logged", {
        min: fmin(e.min),
        fallback: fmin(e.min) + " logged",
      });
      const head = [byCode(taxActivity, e.type), e.desc]
        .filter(Boolean)
        .join(" — ");
      tlEvents.push({
        date: e.date,
        type: "time",
        actorId: e.coId,
        icon: <Clock size={10} />,
        text: head ? head + " · " + logged : logged,
      });
    });

  (cs.docs || []).forEach((d: any) => {
    tlEvents.push({
      date: d.date,
      type: "doc",
      icon: <FileText size={10} />,
      text: t("case.timeline.document", {
        name: d.name,
        fallback: "Document: " + d.name,
      }),
    });
  });

  (cs.stages || []).forEach((st: any) => {
    if (!st.startDate) return;
    const stType = resolveTaxonomyLabel(st.type, taxCaseStage) || st.type || "";
    const rg = st.registerNumber ? " — RG " + st.registerNumber : "";
    tlEvents.push({
      date: st.startDate,
      type: "stage",
      icon: <ChevronRight size={10} />,
      text: t("case.timeline.stage_started", {
        type: stType,
        rg,
        fallback: "Stage started: " + stType + rg,
      }),
    });
  });

  // Chat messages for this case — kind-branched key.
  const caseChat = (chatMsgs || {})[chatKey] || [];
  caseChat.forEach((m: any) => {
    const mc = co.find((c: any) => c.id === m.author);
    const ini = mc ? coIni(mc) : "?";
    tlEvents.push({
      // 207B §3 [ADVISOR] — the LOCAL day and the moment (src/lib/chatTime `chatRowMoment`).
      ...chatRowMoment(m.ts),
      type: "chat",
      icon: <MessageCircle size={10} />,
      /* BRIEF 102 §1a — KEPT, second branch: a chat snippet composed INTO a
         timeline sentence, rendered at :674 in a span that wraps at
         `lineHeight: 1.4` and clips nothing. Unlike SearchPopover's twin, there
         is no bounded box here for the CSS to work against. */
      text:
        ini +
        ": " +
        (m.text && m.text.length > 60
          ? m.text.slice(0, 60) + "…"
          : m.text || ""),
    });
  });

  buildMailEvents(mailings, t).forEach((ev) => tlEvents.push(ev));

  // Phase 28 (item B) — accountability log entries, absorbed into the SAME
  // event list (one entry per save, grouped by groupId) so they share the
  // existing pagination/collapse rather than forming a parallel list.
  // SC3-R5 — helpers resolve field labels, entity nouns, and enum old→new
  // values to the active language before the per-language templates compose.
  // (byCode + DATE_VALUE_FIELDS are defined once near the top of the component.)
  const tlHelpers = {
    entityNoun: (e: any) =>
      t("timeline.entity." + ((ENTITY_SLUG as any)[e] || "item"), {
        fallback: (ENTITY_FALLBACK as any)[e] || e || "the item",
      }),
    fieldLabel: (f: any) => {
      // BRIEF 87 §1e — the ratio field carries the collaborator id, so it must
      // NOT reach the `"timeline.field." + f` key builder below: that would mint
      // `timeline.field.caseCollaborator.7.ratio`, a key per collaborator per
      // firm, unseedable by construction and unbounded. One stable key, with the
      // person concatenated after it — the name is a proper noun and does not
      // belong inside a translated string anyway.
      const m = RATIO_FIELD_RX.exec(String(f || ""));
      if (m) {
        const who = (co || []).find((c: CoRef) => String(c.id) === m[1]);
        const label = t("timeline.field.collaborator_ratio", { fallback: "billing ratio" });
        return `${label} (${who ? (who.initials || who.name || who.lastName || "#" + m[1]) : "#" + m[1]})`;
      }
      return t((FIELD_KEY as any)[f] || "timeline.field." + f, {
        fallback: (FIELD_LABELS as any)[f] || humanizeField(f, co),
      });
    },
    resolveValue: (entity: any, field: any, raw: any) => {
      if (raw == null || raw === "∅" || raw === "") return raw;
      // T2 — format date/datetime values to Belgian DD/MM/YYYY (no raw ISO, no
      // time component), reusing the formatter the date column uses.
      if (DATE_VALUE_FIELDS.has(field)) return fd(raw);
      if (field === "caseEisenhower") return eisenLabel(raw) || raw;
      if (field === "assigned" || field === "delegatedAssigned")
        return String(raw)
          .split(",")
          .map((s) => {
            const id = parseInt(s.trim(), 10);
            const c = (co || []).find((x: any) => x.id === id);
            return c
              ? [c.firstName, c.lastName].filter(Boolean).join(" ") ||
                  c.email ||
                  s.trim()
              : s.trim();
          })
          .join(", ");
      if (field === "status")
        return entity === "Task"
          ? getTaskStatusMeta(raw, taxTaskStatus)?.label || raw
          : resolveTaxonomyLabel(raw, taxCaseStatus) || raw;
      // No `entity === "Case"` arm: Case has no priority column, `ALLOWED`
      // accepts no priority write and CASE_LOG_FIELDS logs none, so a
      // chronology row carrying (Case, priority) cannot exist. The arm that
      // stood here read the retired case_priority taxonomy and never ran.
      if (field === "priority") return byCode(taxTaskPriority, raw);
      if (field === "type") {
        if (entity === "Task") return byCode(taxActivity, raw);
        if (entity === "AgendaEvent") return byCode(taxEventType, raw);
        return resolveTaxonomyLabel(raw, taxCaseType) || raw;
      }
      return raw;
    },
  };
  buildEditEvents(chronicle, t, tlHelpers).forEach((ev) => tlEvents.push(ev));

  // Sort newest-first — 207B §3: within a day, a chat row by its time
  // (src/lib/chatTime `newestFirst`).
  tlEvents.sort(newestFirst);

  // ── 186 (d) — the filter (the sentence and the actor, case-blind), then the pagination over what it leaves.
  const needle = q.trim().toLowerCase();
  const shownEvents = needle
    ? tlEvents.filter((ev) => String(ev.text ?? "").toLowerCase().includes(needle) || String(ev.actorName ?? "").toLowerCase().includes(needle))
    : tlEvents;

  // ── Pagination ─────────────────────────────────────────────────────────────
  const { cur, tot, pageItems } = pageOf(shownEvents, pager.page);
  const pageEvents = sectionOpen ? pageItems : [];

  /* 186 (d) [RULED 186 (16)] — ONE EVENT ROW, the timeline's own markup (the
     dot, the date, the icon, the actor, the sentence), rendered by the list
     and — `oneLine` — by the closed section for its most recent event: the
     sentence on one line, truncated, the whole of it on hover. No verb on a
     row, open or closed: the Chronology is read, never edited. */
  const renderEvent = (ev: TimelineEvent, oneLine = false) => (
    <div
      style={{
        display: "flex",
        gap: 6,
        alignItems: "flex-start",
        padding: "3px 0",
        position: "relative",
        ...(oneLine ? { minWidth: 0 } : {}),
      }}
    >
      <div
        style={{
          position: "absolute",
          left: -17,
          top: 6,
          width: 8,
          height: 8,
          borderRadius: RAD.pill,
          background: (TL_COLORS as any)[ev.type] || "var(--tm)",
          border: "2px solid var(--bd)",
        }}
      />
      <span
        style={{
          fontSize: FS.s9_5,
          color: "var(--tm)",
          flexShrink: 0,
          minWidth: 48,
        }}
      >
        {fd(ev.date)}
        {/* 207B §3 — a chat row's clock after its date, in italics, as every chat time. */}
        {ev.ts != null && (
          <>
            {" "}
            <ChatClock ts={ev.ts} />
          </>
        )}
      </span>
      <span
        style={{
          color: (TL_COLORS as any)[ev.type] || "var(--tm)",
          flexShrink: 0,
        }}
      >
        {ev.icon}
      </span>
      {(ev.type === "edit" || ev.type === "time") &&
        (() => {
          // Render the actor as an InitialsBadge (resolved from
          // collaborators), falling back to the snapshot name.
          // 158 §7 — a time entry's collaborator rides the same
          // slot: it is the same fact about the same row.
          const actor =
            ev.actorId != null
              ? co.find((x: any) => x.id === ev.actorId)
              : null;
          if (actor) {
            return (
              <InitialsBadge
                person={actor}
                isMe={
                  currentUser != null && actor.id === currentUser
                }
                size={16}
                fontSize={8}
                style={{ flexShrink: 0 }}
              />
            );
          }
          if (ev.actorName) {
            return (
              <span
                style={{
                  fontSize: FS.s10_5,
                  color: "var(--tp)",
                  fontWeight: 600,
                  flexShrink: 0,
                }}
              >
                {ev.actorName}
              </span>
            );
          }
          return null;
        })()}
      <span
        title={oneLine ? ev.text : undefined}
        style={{
          fontSize: FS.s10_5,
          color: "var(--tp)",
          lineHeight: 1.4,
          ...(oneLine ? { minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } : {}),
        }}
      >
        {ev.text}
      </span>
    </div>
  );
  /* 186 (d) [RULED 186 (16)] — A CLOSED SECTION SHOWS ITS LAST ROW: the most
     recent event, in the collapsed shape B.8 built for the Notes section —
     content UNDER the collapsed header (never in `rightContent`, which
     SectionHeader hides), a direct child carrying `.ixLast`, the one class
     App.css excepts from the Phase 25.1 spine's CSS half. No lock, no add, no
     search, no pager, no verb: this is ruled, not an omission. */
  const lastEvent = !sectionOpen && tlEvents.length > 0 ? tlEvents[0] : null;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="csSec-timeline" data-secollapsed={sectionOpen ? "0" : "1"}>
      <SectionHeader
        title={t("case.timeline", { fallback: "Timeline" })}
        collapsible
        open={!!sectionOpen}
        onToggle={onSectionToggle}
        rightContent={
          <ListNotLoaded lists={TL_LISTS} variant="badge">
            <span style={{ fontSize: FS.s9, color: "var(--tm)" }}>
              {t("case.timeline.events_count", {
                count: tlEvents.length,
                fallback:
                  tlEvents.length + " event" + (tlEvents.length === 1 ? "" : "s"),
              })}
            </span>
          </ListNotLoaded>
        }
      />

      {sectionOpen && (
        <ListNotLoaded lists={TL_LISTS}>
          <SectionSearch value={q} onChange={setQ} />
          <div
            style={{
              borderLeft: "2px solid var(--br)",
              marginLeft: 6,
              paddingLeft: 12,
            }}
          >
            {tlEvents.length === 0 && (
              <p style={{ fontSize: FS.s11, color: "var(--tm)" }}>
                {t("case.timeline.empty", { fallback: "No events recorded" })}
              </p>
            )}
            {(() => {
              let lastMonth = "";
              return pageEvents.map((ev, i) => {
                const d = new Date(ev.date);
                const mk = d.toLocaleString(activeLocale(), {
                  month: "short",
                  year: "numeric",
                });
                const showMo = mk !== lastMonth;
                lastMonth = mk;
                return (
                  <div key={i}>
                    {showMo && (
                      <div
                        style={{
                          fontSize: FS.s9,
                          color: "var(--tm)",
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: ".3px",
                          margin: "8px 0 4px",
                        }}
                      >
                        {mk}
                      </div>
                    )}
                    {renderEvent(ev)}
                  </div>
                );
              });
            })()}
            {tot > 1 && <Pager cur={cur} tot={tot} setPage={pager.setPage} />}
          </div>
        </ListNotLoaded>
      )}
      {lastEvent && (
        <ListNotLoaded lists={TL_LISTS} variant="inline">
          <div
            className="ixLast"
            style={{
              borderLeft: "2px solid var(--br)",
              marginLeft: 6,
              paddingLeft: 12,
            }}
          >
            {renderEvent(lastEvent, true)}
          </div>
        </ListNotLoaded>
      )}
    </div>
  );
}
